/**
 * Formatting helpers.
 *
 * Money is formatted FROM THE STRING the API returns. numeric(18,6) does not
 * survive a round trip through a JS double and the loss is silent, so nothing
 * here ever calls parseFloat on an amount (doc 07 section 3).
 */
export function formatMoney(amount: string | null, currency: string | null): string {
  if (amount === null) return '—';
  const negative = amount.startsWith('-');
  const [whole = '0', frac = ''] = amount.replace('-', '').split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const cents = (frac + '00').slice(0, 2);
  const symbol = currency === 'ZAR' ? 'R' : currency ? currency + ' ' : '';
  return `${negative ? '-' : ''}${symbol}${grouped}.${cents}`;
}

/** A unit price to the cent, or to the precision it was priced at when that is finer: R0.4575. */
export function formatUnitPrice(amount: string, currency: string | null): string {
  const [whole = '0', frac = ''] = amount.split('.');
  const digits = frac.replace(/0+$/, '');
  return digits.length <= 2
    ? formatMoney(amount, currency)
    : `${formatMoney(whole, currency).slice(0, -3)}.${digits}`;
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Timestamps render in the TENANT's timezone with an explicit label, never the
 * browser's. A South African admin looking at a tenant configured for Namibia
 * must see Namibian time or the call records are misleading (doc 07 section 3).
 */
export function formatDateTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: timezone,
  }).format(new Date(iso));
}

export function formatTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-ZA', {
    timeStyle: 'medium', timeZone: timezone,
  }).format(new Date(iso));
}

export function timezoneAbbr(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-ZA', { timeZone: timezone, timeZoneName: 'short' })
    .formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timezone;
}

export const formatNumber = (n: number): string =>
  n.toLocaleString('en-ZA').replace(/,/g, ' ');

/**
 * Handles BOTH directions.
 *
 * It used to assume the past, so a future timestamp produced a negative
 * difference, fell through `mins < 1`, and rendered as "just now" -- which is
 * how a registration expiring in four minutes came to read as though it had
 * just expired.
 */
export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const future = diff < 0;
  const mins = Math.round(Math.abs(diff) / 60000);
  const say = (v: string): string => (future ? `in ${v}` : `${v} ago`);

  if (mins < 1) {
    const secs = Math.round(Math.abs(diff) / 1000);
    if (secs < 10) return 'just now';
    return say(`${secs}s`);
  }
  if (mins < 60) return say(`${mins}m`);
  const hours = Math.round(mins / 60);
  if (hours < 24) return say(`${hours}h`);
  return say(`${Math.round(hours / 24)}d`);
}

/**
 * File sizes, at the precision a person reading a list actually wants.
 *
 * One decimal below 10 units and none above, so "1.4 MB" and "18 MB" rather
 * than "1.4323 MB" -- the extra digits are noise when the question being asked
 * is "is this the scan or the thumbnail?".
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/**
 * The wall clock in a timezone: today's date (YYYY-MM-DD), the time (HH:MM) and the
 * weekday (0 = Sunday). For "is it open right now" answers, which have to use the
 * schedule's own clock -- the browser's, or UTC's date, is wrong between midnight
 * and 02:00 in South Africa (UX review, 21 October 2026). An unknown zone falls back
 * to Johannesburg, where every account so far lives.
 */
export function wallClock(timezone: string | null | undefined, at: Date = new Date()): { date: string; time: string; dow: number } {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'Africa/Johannesburg', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
    }).formatToParts(at);
  } catch {
    return wallClock('Africa/Johannesburg', at);
  }
  const p = (t: string) => parts.find((x) => x.type === t)?.value ?? '';
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p('weekday'));
  return { date: `${p('year')}-${p('month')}-${p('day')}`, time: `${p('hour')}:${p('minute')}`, dow };
}
